import TerminalTreeViewModel from "../modelTreeViews/TerminalTreeviewModel";
import BaseModel from "./BaseModel";

import BaseObject from "./BaseObject";

import { copyAllProperties } from "./utils";

import Logger from "../Logger";

const logger = new Logger();
//////////////////////////////////////////////////
import ObjectFactory from "./ObjectFactory";
import Consumer from "./Consumer";
import type TerminalStrip from "./TerminalStrip";
import type Channel from "./Channel";
import type Fuse from "./Fuse";
import type ELCB from "./ELCB";

import type TerminalBlock from "./TerminalBlock";
import { TerminalStripVoltageLevel } from "./Consumer";

ObjectFactory.registerClass("Terminal", (data?: any, parent?: BaseModel) => {
  return new Terminal({});
});
//////////////////////////////////////////////////
export type TerminalListBridgeInfo = {
  bridgeIn: boolean;
  bridgeOut: boolean;
  dot: boolean;
  obj: BaseModel;
};

export default class Terminal extends BaseModel {
  public get className(): string {
    return "Terminal"; // no itx
  }

  public get isTerminal(): boolean {
    return true;
  }

  getTagPrefix(): string {
    return ":"; // Perfix für Terminal ist IMMER ":", unabhängig von der Einstellung "DIN" oder "Einfach/DOT"  in den Settings
  }

  ///////////////////////////////////////
  /// nonpersistent, for lists only !!!!
  elcbInfos?: Array<TerminalListBridgeInfo>;
  fuseInfos?: Array<TerminalListBridgeInfo>;
  ///////////////////////////////////////

  terminalIndex: number = 0; // default für mainTerminal

  ///////////////////////////////////////////////////////
  //
  // eine KlemmenBlock wird gebildet aus
  //
  // 8   addOn = false
  // 9 sw addOn = true, singleWire = true
  // 9 gr addOn = true, singleWire = true

  // -----------------------------------------------------------------------------
  // addOn
  // -----------------------------------------------------------------------------
  _addOn: boolean = false;
  get addOn(): boolean {
    return this._addOn;
  }

  set addOn(newValue: boolean) {
    if (this._addOn === newValue) return;
    this.beforeChange("addOn");
    this._addOn = newValue;
    this.changed("addOn");
  }

  // -----------------------------------------------------------------------------
  // singleWire
  // -----------------------------------------------------------------------------
  _singleWire: boolean = false;
  get singleWire(): boolean {
    return this._singleWire;
  }

  set singleWire(newValue: boolean) {
    if (this._singleWire === newValue) return;
    this.beforeChange("singleWire");
    this._singleWire = newValue;
    this.changed("singleWire");
  }

  // -----------------------------------------------------------------------------

  //////////////////////////////////////////////////////

  isLast?: boolean;
  get isFirst(): boolean {
    return this.isMasterTerminal;
  }

  // -----------------------------------------------------------------------------
  // installationsStatus
  // -----------------------------------------------------------------------------
  _installationsStatus: boolean = false;

  set installationsStatus(status: boolean) {
    this.beforeChange("installationsStatus");
    this._installationsStatus = status;

    this.changed("installationsStatus");
  }

  get installationsStatus(): boolean {
    return this._installationsStatus;
  }

  // -----------------------------------------------------------------------------

  constructor(data?: any, parent?: BaseObject) {
    super(data, parent);
    copyAllProperties(data, this);
    this.treeViewModel = new TerminalTreeViewModel(this);
  }

  getPersistentProperties(): string[] {
    return ["installationsStatus", "addOn", "singleWire", ...super.getPersistentProperties()]; // no itx
  }

  delete(): Array<BaseObject> {
    this.isDeleting = true;

    var res = new Array<BaseObject>();
    // delete all subTerminals if masterTerminal
    if (this.isMasterTerminal) {
      const terminalBlock = this.getTerminalBlock();
      if (terminalBlock) {
        terminalBlock.subTerminals.forEach((subTerminal) => {
          subTerminal.delete();
        });
      }
    }

    const consumer = this.consumer;
    if (consumer) {
      consumer.terminal = undefined;
    }

    res = res.concat(super.delete());
    return res;
  }

  //
  // readonly backPointer to Consumer for consumer.terminal
  // to set this property use consumer.terminal = ...
  //

  // -----------------------------------------------------------------------------
  // consumer
  // -----------------------------------------------------------------------------
  _consumer: Consumer | undefined;
  get consumer(): Consumer | undefined {
    return this._consumer;
  }

  // -----------------------------------------------------------------------------

  //
  // suche von diesem Teminal ausgehen "nach oben" den ersten Consumer,
  // Wenn es dieses Terminal selbst ist dann wird dieser zurückgegeben
  // wenn es dieses Teminal selbst ist, dann ist der wireIndexWithoutPEandN = 0
  //
  // wenn es "ein terminal weiter oben ist", dann ist der wireIndexWithoutPEandN = 1 // also die 2. Ader
  //
  getWireAndConsumer():
    | {
        consumer: Consumer;
        consumerWireIndexWithoutPEandN: number;
      }
    | undefined {
    const tb = this.getTerminalBlock();
    let wi = 0;
    if (tb) {
      let ti = this.terminalIndex;
      let t: Terminal | undefined = this;

      while (ti >= 0 && t) {
        if (t.consumer) {
          const m = t.consumer.wireRequirement.numberOfLWiresWithoutPEandN;
          if (wi >= m) {
            //
            // es ist eine Consumer "weiter oben" aufgelegt,
            //
            // aber der Consumer benutzt zB nur 1 Ader (m = 1)
            // und der wireIndexWithoutPEandN wäre zb 1 == (Ader 2)
            //
            // dann wird diese Ader nicht mehr von dem Consumer benutzt
            // und wir geben undefined zurück
            //
            return undefined; // zu viele Adern abgefragt
          }

          return {
            consumer: t.consumer,
            consumerWireIndexWithoutPEandN: wi,
          };
        }

        ti--;
        wi++;
        t = tb.terminalFromIndex(ti);
      }
    }

    return undefined;
  }

  get channel(): Channel | undefined {
    const consumer = this.consumer;
    if (consumer) {
      return consumer.getConnectedChannel();
    }
    return undefined;
  }

  get maxTagCharacters(): number {
    return 6; // for Terminal max tag 6 because of consumer with 3 Terminals( X11-13)
  }

  emptyTagIsForbidden(): boolean {
    return true;
  }

  canBeEditedInteractive(): boolean {
    return false;
  }

  canBeMovedToNewParent(newParent: BaseModel) {
    // in same cabinet possible if not forbidden
    return newParent.parent == this.parent?.parent;
  }

  getParentChildrenGroup(): Array<BaseModel> | undefined {
    return (this.parent as TerminalStrip)?.terminals.sortBy("tag");
  }

  get fuse(): Fuse | undefined {
    return this.consumer?.fuse;
  }

  get elcb(): ELCB | undefined {
    return this.fuse?.parent as ELCB;
  }

  ///////////////////////////////////////////////////////////
  get wireColorCode(): string {
    return this.consumer?.terminalViaWireName || "";
  }

  get wireHtmlColor(): string {
    const tb = this.getTerminalBlock();
    if (!tb) {
      return "#fff";
    }
    const cableType = tb.cableType;
    const wireDefinitions = cableType?.wireDefinitions;

    let wireIndexFromTerminal = this.terminalIndex;
    let wireIndex = 2;
    const mainConsumer = tb.mainTerminal.consumer;
    if (mainConsumer) {
      wireIndex = mainConsumer.fullWireIndex(wireIndexFromTerminal);
    }

    let wireName = "";
    let wireHtmlColor = "#fff";

    if (wireIndex > 0 && wireDefinitions) {
      wireName = wireDefinitions[wireIndex]?.longName || "?Ader?";
      wireHtmlColor = cableType.htmlColors?.[wireIndex] || "#eee";
    }
    return wireHtmlColor;
  }

  ///////////////////////////////////////////////////////////

  //
  // liefert für eine AddOn Klemme die MasterKlemme
  //  also für 12sw von (11, 12sw, 12gr) die 11
  //
  get masterTerminal(): Terminal {
    if (this.addOn && this.parent) {
      const mainTerminal = this.getTerminalBlock()?.mainTerminal;
      if (mainTerminal) {
        return mainTerminal;
      }
      const terminals = (this.parent as TerminalStrip).terminals.sortBy("tag");
      const i = terminals.indexOf(this);

      if (i > 0) {
        return terminals[i - 1];
      }
    }

    return this;
  }

  get isMasterTerminal(): boolean {
    return !this.addOn;
  }

  get isAddOnTerminal(): boolean {
    return this.addOn;
  }

  get size(): number {
    // size from cable type

    const withNeutral = (this.parent as TerminalStrip)?.voltageLevel == TerminalStripVoltageLevel.V230 ? 1 : 0;
    const numberDeckTerminal = 2;

    if (this.isMasterTerminal) {
      if (this.consumer?.cableType) {
        return Math.ceil((this.consumer.cableType.numberOfWires - withNeutral) / numberDeckTerminal);
      }
      // size from connected consumer
      if (this.consumer?.wireRequirement.numberOfLWiresWithoutPEandN) {
        return Math.ceil(this.consumer.wireRequirement.numberOfLWiresWithoutPEandN / numberDeckTerminal);
      }
    }
    // else size from tag
    return this.tagSize;
  }

  insertTerminal(tag: string, addOn: boolean, isSingleWire: boolean): Terminal | undefined {
    try {
      const terminalStrip = this.parent as TerminalStrip;
      if (terminalStrip) {
        const newTerminal = new Terminal({ tag: tag }, this.parent);
        newTerminal.addOn = addOn;
        newTerminal.singleWire = isSingleWire;

        terminalStrip.terminals.add(newTerminal);

        return newTerminal;
      }
    } catch (ex) {
      console.error(ex);
    }

    return undefined;
  }

  //
  // cache für getTerminalBlock zur SpeedOptimierung !!!!
  //
  // undefinded: noch nicht berechnet
  // null: kein TerminalBlock gefunden
  // sonst TerminalBlock
  //
  _terminalBlock: TerminalBlock | undefined | null;

  getTerminalBlock(): TerminalBlock | undefined {
    if (this._terminalBlock !== undefined) {
      return this._terminalBlock || undefined;
    }

    const terminalBlocks = (this.parent as TerminalStrip)?.getTerminalBlocks();
    let t = terminalBlocks.find((tb) => tb.mainTerminal == this);
    if (t) {
      this._terminalBlock = t;
      return t;
    }

    t = terminalBlocks.find((tb) => {
      for (const subTerminal of tb.subTerminals) {
        if (subTerminal == this) {
          return true;
        }
      }
      return false;
    });

    this._terminalBlock = t;
    return t;
  }

  //
  // hier wird ein Terminal "11,12" in 3 Terminals "11, 12ge, 12sw" aufgeteilt
  //
  splitIntoSingleTerminals() {
    const terminalBlock = this.getTerminalBlock();
    terminalBlock?.splitGroupTerminalIntoSingleTerminals();
  }
}
